import { HOTSTUFF_NETWORK_MAINNET, HOTSTUFF_NETWORK_TESTNET } from "./constants";

/**
 * Thin wrappers around Hotstuff's REST surface.
 *
 * The backend uses these to post pre-signed `/exchange` payloads (the frontend
 * signs the EIP-712 envelope with the user's wallet). For reads we also expose
 * `postHotstuffInfo`, primarily for liveness checks and agent-list queries.
 *
 * NOTE: full trading flows should prefer `@hotstuff-labs/ts-sdk` — it handles
 * msgpack encoding, nonces, and signature wrapping. These helpers are only for
 * the pre-signed approval path where the backend never holds the master
 * wallet's private key.
 */

const HOTSTUFF_MAINNET_BASE_URL = "https://api.hotstuff.trade" as const;
const HOTSTUFF_TESTNET_BASE_URL = "https://testnet-api.hotstuff.trade" as const;

export interface PostHotstuffOptions {
  isTestnet?: boolean;
  /** Override for testing / proxying. */
  baseUrl?: string;
  /** Request timeout in milliseconds. Defaults to 10s. */
  timeoutMs?: number;
}

const resolveBaseUrl = (opts?: PostHotstuffOptions): string => {
  if (opts?.baseUrl) return opts.baseUrl;
  return opts?.isTestnet ? HOTSTUFF_TESTNET_BASE_URL : HOTSTUFF_MAINNET_BASE_URL;
};

/** Convenience: the `source` field embedded in signed-action envelopes. */
export const hotstuffSource = (
  isTestnet?: boolean,
): typeof HOTSTUFF_NETWORK_MAINNET | typeof HOTSTUFF_NETWORK_TESTNET =>
  isTestnet ? HOTSTUFF_NETWORK_TESTNET : HOTSTUFF_NETWORK_MAINNET;

/**
 * POST a pre-signed payload to Hotstuff's `/exchange` endpoint.
 *
 * The payload shape matches the upstream SDK:
 * ```json
 * { "action": { "data": {...}, "type": "addAgent" }, "signature": "0x...", "nonce": 1712345678900 }
 * ```
 * Throws if Hotstuff returns a non-2xx response or a JSON body with `error`.
 */
export async function postHotstuffExchange<T = unknown>(
  body: Record<string, unknown>,
  opts?: PostHotstuffOptions,
): Promise<T> {
  const url = `${resolveBaseUrl(opts)}/exchange`;
  const controller = new AbortController();
  const timeoutMs = opts?.timeoutMs ?? 10_000;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const serializedBody = JSON.stringify(body);
    const response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: serializedBody,
      signal: controller.signal,
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(`Hotstuff /exchange returned HTTP ${response.status}: ${text}`);
    }
    const parsed = text ? (JSON.parse(text) as T & { error?: string; type?: string }) : (undefined as T);
    if (parsed && typeof parsed === "object" && "error" in parsed && (parsed as { error?: string }).error) {
      throw new Error(`Hotstuff /exchange error: ${(parsed as { error: string }).error}`);
    }
    if (parsed && typeof parsed === "object" && (parsed as { type?: string }).type === "error") {
      throw new Error(`Hotstuff /exchange error: ${JSON.stringify(parsed)}`);
    }
    return parsed as T;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * POST a read request to Hotstuff's `/info` endpoint.
 */
export async function postHotstuffInfo<T = unknown>(
  body: { method: string; params?: Record<string, unknown> },
  opts?: PostHotstuffOptions,
): Promise<T> {
  const url = `${resolveBaseUrl(opts)}/info`;
  const controller = new AbortController();
  const timeoutMs = opts?.timeoutMs ?? 10_000;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(`Hotstuff /info returned HTTP ${response.status}: ${text}`);
    }
    return text ? (JSON.parse(text) as T) : (undefined as T);
  } finally {
    clearTimeout(timeout);
  }
}
